// src/views/UserViews/insights/components/FleetStatusSection.tsx
//
// Four current-status MetricCards with no date filter - identical at every range preset,
// hence the SectionCard subtitle telling the user these counts are current status and do
// not follow the date-range selector. That line is now the only place that meaning lives:
// the restyle dropped the "- current" qualifier from the heading, so it must not be
// removed. It sits inside the outer SectionCard so all four metrics share one time-scope
// grouping, rather than floating on the gray page canvas. Lead ("Current Status") is bold;
// the qualifier ("not reflective of selected date range") is italic.
//
// Data sources differ per card. "Active Now" counts distinct users currently active from
// the SignalR user-status pings (`liveActiveUsers`, see useLiveActiveUsers.ts), the same
// source as the User Dashboard's "Active" chip - so it is the only card whose copy may say
// "live". Clicking it opens ActiveUsersDialog, which lists those same users. The other three are `now64(3)`-relative DeviceLatestStatus measures (Task
// 4.2), whose capture path has no live-60s guarantee (catalog §0): their copy must not
// claim "live".
//
// Rule 5: active now / reported today / offline >24h / low battery are independent,
// overlapping filters, not an exclusive partition - a device can be low battery AND
// recently active. This section deliberately renders four standalone cards with no shared
// progress bar, donut, or "% of total" subtext that would imply they sum to the total
// reporting device count (that exclusive partition is the donut, built in M5 from a
// genuinely exclusive query). The outer SectionCard groups TIME SCOPE only.

import { useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import type { InsightsSectionState } from '@/hooks/insights/useInsightsData';
import type { FleetStatus } from '@/models/Insights/InsightsTypes';
import type { LiveActiveUser } from '@/models/Insights/liveActiveUsers';
import ActiveUsersDialog from './ActiveUsersDialog';
import MetricCard from './MetricCard';
import SectionCard from './SectionCard';

// One step darker than `INSIGHTS_CARD_BORDER_COLOR` (`#E5E7EB`) so the outer wrapper
// reads as a group around the nested MetricCards without leaving the Insights gray scale.
const FLEET_STATUS_WRAPPER_BORDER_COLOR = '#D1D5DB';

type FleetStatusSectionProps = {
  status: InsightsSectionState<FleetStatus>;
  liveActiveUsers: InsightsSectionState<LiveActiveUser[]>;
};

export default function FleetStatusSection({ status, liveActiveUsers }: FleetStatusSectionProps) {
  const { t } = useTranslation();
  const { data, loading, error } = status;
  const activeUsers = liveActiveUsers.data;
  const [isActiveUsersDialogOpen, setIsActiveUsersDialogOpen] = useState(false);

  return (
    <SectionCard
      title=""
      subtitle={
        <span
          data-testid="fleet-status-subcaption"
          aria-label={t(StringKeys.INSIGHTS_FLEET_STATUS_SUBCAPTION)}
        >
          <Box
            component="span"
            sx={{ fontWeight: 700 }}
            data-testid="fleet-status-subcaption-lead"
          >
            {t(StringKeys.INSIGHTS_FLEET_STATUS_SUBCAPTION_LEAD)}
          </Box>
          {' - '}
          <Box
            component="span"
            sx={{ fontStyle: 'italic' }}
            data-testid="fleet-status-subcaption-detail"
          >
            {t(StringKeys.INSIGHTS_FLEET_STATUS_SUBCAPTION_DETAIL)}
          </Box>
        </span>
      }
      subtitleVariant="body1"
      autoHeight
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
        }}
      >
        <MetricCard
          testId="fleet-recently-active"
          label={t(StringKeys.INSIGHTS_RECENTLY_ACTIVE_LABEL)}
          caption={t(StringKeys.INSIGHTS_RECENTLY_ACTIVE_CAPTION)}
          value={activeUsers?.length ?? null}
          format="count"
          loading={liveActiveUsers.loading}
          error={liveActiveUsers.error}
          tone="positive"
          onClick={activeUsers ? () => setIsActiveUsersDialogOpen(true) : undefined}
        />
        <MetricCard
          testId="fleet-reported-today"
          label={t(StringKeys.INSIGHTS_REPORTED_TODAY_LABEL)}
          caption={t(StringKeys.INSIGHTS_REPORTED_TODAY_CAPTION)}
          value={data?.reportedTodayUtc ?? null}
          format="count"
          loading={loading}
          error={error}
          tone="neutral"
        />
        <MetricCard
          testId="fleet-not-seen-24h"
          label={t(StringKeys.INSIGHTS_NOT_SEEN_24H_LABEL)}
          caption={t(StringKeys.INSIGHTS_NOT_SEEN_24H_CAPTION)}
          value={data?.offlineOver24h ?? null}
          format="count"
          loading={loading}
          error={error}
          tone="warning"
        />
        {/* The "devices with an unknown battery level are excluded" qualifier lives in its
            own `tooltip` prop, separate from `caption` - the exclusion is a real statement
            about the denominator, so it is kept, never dropped. `MetricCard` merges the two
            into a single combined tooltip revealed by hovering/focusing/tapping the whole
            card (rather than exposing two overlapping hover targets: the label's own info
            icon plus the whole card), the same treatment `MetricCard` applies to Utilization's
            "Protected hours" and Safety's "Time muted" cards, which have the same
            caption-plus-tooltip shape. */}
        <MetricCard
          testId="fleet-low-battery"
          label={t(StringKeys.INSIGHTS_LOW_BATTERY_LABEL)}
          caption={t(StringKeys.INSIGHTS_LOW_BATTERY_CAPTION)}
          tooltip={t(StringKeys.INSIGHTS_LOW_BATTERY_TOOLTIP)}
          value={data?.lowBattery ?? null}
          format="count"
          loading={loading}
          error={error}
          tone="warning"
        />
      </Box>
      <ActiveUsersDialog
        open={isActiveUsersDialogOpen}
        onClose={() => setIsActiveUsersDialogOpen(false)}
        users={activeUsers ?? []}
      />
    </SectionCard>
  );
}
